import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, User, MessageCircle } from "lucide-react";
import { LINKS, SERVICE_GROUPS, waLink } from "@/lib/links";

function isExternalHref(href) {
  if (!href || typeof href !== "string") return false;
  if (href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("#") || href.startsWith("/")) return false;
  if (href.startsWith("https://wa.me") || href.startsWith("http://wa.me")) return true;
  try {
    const u = new URL(href, "https://www.printlivros.com.br");
    return !u.hostname.replace(/^www\./, "").endsWith("printlivros.com.br");
  } catch {
    return false;
  }
}

const SiteLink = ({ href, testid, className, children, onClick }) => {
  const external = isExternalHref(href);
  return (
    <a
      href={href}
      data-testid={testid}
      className={className}
      onClick={onClick}
      {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
    >
      {children}
    </a>
  );
};

export default function Navbar() {
  const [drawer, setDrawer] = useState(false);
  const [mega, setMega] = useState(false);

  return (
    <>
      <header className="fixed top-3 inset-x-3 sm:inset-x-6 z-[100]" data-testid="main-navbar">
        <div className="relative glass rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.12)] max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button data-testid="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2.5 group">
            <img src="./logo.png" alt="Print Livros — Gráfica & Editora" data-testid="nav-logo-img" className="h-11 w-auto rounded-lg shadow-sm transition-transform duration-300 group-hover:scale-105" />
            <img src="./logo-texto.png" alt="" data-testid="nav-logo-texto" className="h-6 sm:h-7 w-auto hidden min-[420px]:block" />
          </button>

          <nav className="hidden lg:flex items-center gap-6 text-[15px] font-semibold text-neutral-700">
            <SiteLink testid="nav-home" href={LINKS.home} className="hover:text-blue-600 transition-colors">Home</SiteLink>
            <div className="relative" onMouseEnter={() => setMega(true)} onMouseLeave={() => setMega(false)}>
              <button data-testid="nav-services" className="flex items-center gap-1 hover:text-blue-600 transition-colors py-2">
                Serviços <ChevronDown size={14} className={`transition-transform duration-300 ${mega ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {mega && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, x: "-50%" }}
                    animate={{ opacity: 1, y: 0, x: "-50%" }}
                    exit={{ opacity: 0, y: 8, x: "-50%" }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-1/2 pt-3 w-[760px] max-w-[calc(100vw-3rem)]"
                    data-testid="services-mega-menu"
                  >
                    <div className="glass rounded-2xl p-6 grid grid-cols-3 gap-x-6 gap-y-5 shadow-xl">
                      {SERVICE_GROUPS.map((g) => (
                        <div key={g.title}>
                          <p className="text-[11px] font-bold tracking-[0.18em] text-blue-700 uppercase mb-2">{g.title}</p>
                          <ul className="space-y-1">
                            {g.links.slice(0, 4).map((l) => (
                              <li key={l.url}>
                                <SiteLink
                                  testid={`mega-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                  href={l.url}
                                  className="text-[13.5px] font-semibold text-neutral-700 hover:text-blue-700 transition-colors leading-tight block"
                                >
                                  {l.label}
                                </SiteLink>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                      <div className="col-span-3 border-t border-neutral-200/70 pt-3 flex items-center justify-between">
                        <SiteLink testid="mega-quote-cta" href={LINKS.orcamento} className="text-sm font-bold text-blue-700 hover:text-blue-900 flex items-center gap-1.5">
                          Simular preço na calculadora online
                        </SiteLink>
                        <SiteLink testid="mega-whatsapp" href={waLink("*Print Livros — contato pelo site*\n\nOlá! Gostaria de mais informações.")} className="text-sm font-bold text-green-700 hover:text-green-800 flex items-center gap-1.5">
                          <MessageCircle size={14} /> Falar no WhatsApp
                        </SiteLink>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <SiteLink testid="nav-about" href={LINKS.quemSomos} className="hover:text-blue-600 transition-colors">Quem somos</SiteLink>
            <SiteLink testid="nav-portfolio" href={LINKS.portfolio} className="hover:text-blue-600 transition-colors">Portfólio</SiteLink>
            <SiteLink testid="nav-contact" href={LINKS.contato} className="hover:text-blue-600 transition-colors">Contato</SiteLink>
          </nav>

          <div className="flex items-center gap-2">
            <SiteLink
              testid="nav-login-button"
              href={LINKS.login}
              className="hidden sm:flex items-center gap-1.5 bg-neutral-900 text-white text-sm font-bold px-4 py-2 rounded-full hover:bg-blue-700 transition-colors"
            >
              <User size={14} /> Entrar
            </SiteLink>
            <button data-testid="nav-burger" onClick={() => setDrawer(true)} className="lg:hidden p-2 text-neutral-800" aria-label="Abrir menu">
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {drawer && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-neutral-950/50 backdrop-blur-sm z-[110]"
              onClick={() => setDrawer(false)}
              data-testid="drawer-backdrop"
            />
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 260 }}
              className="fixed top-0 right-0 bottom-0 w-[85vw] max-w-sm bg-[#FDFCF9] z-[120] p-6 flex flex-col overflow-y-auto"
              data-testid="mobile-drawer"
            >
              <div className="flex items-center justify-between mb-6">
                <img src="./logo.png" alt="Print Livros" className="h-10 w-auto rounded-lg" />
                <button data-testid="drawer-close" onClick={() => setDrawer(false)} aria-label="Fechar menu"><X size={22} /></button>
              </div>
              <nav className="flex flex-col gap-1 text-lg font-bold mb-6">
                {[
                  ["Home", LINKS.home, "drawer-link-home"],
                  ["Quem somos", LINKS.quemSomos, "drawer-link-quem-somos"],
                  ["Portfólio", LINKS.portfolio, "drawer-link-portfolio"],
                  ["Contato", LINKS.contato, "drawer-link-contato"],
                  ["Entrar / Login", LINKS.login, "drawer-link-login"],
                  ["Simular orçamento", LINKS.orcamento, "drawer-link-orcamento"],
                ].map(([label, url, tid]) => (
                  <SiteLink key={tid} testid={tid} href={url} className="py-3 border-b border-neutral-200 hover:text-blue-600 transition-colors block">
                    {label}
                  </SiteLink>
                ))}
              </nav>
              <p className="text-[11px] font-bold tracking-[0.2em] text-blue-700 uppercase mb-2">Serviços</p>
              <div className="space-y-3 mb-6">
                {SERVICE_GROUPS.map((g) => (
                  <details key={g.title} className="group">
                    <summary className="font-bold text-neutral-800 cursor-pointer list-none flex items-center justify-between py-1.5" data-testid={`drawer-group-${g.title.toLowerCase().replace(/\s/g, "-")}`}>
                      {g.title} <ChevronDown size={15} className="text-neutral-400 group-open:rotate-180 transition-transform" />
                    </summary>
                    <ul className="pl-3 mt-1 space-y-1.5">
                      {g.links.map((l) => (
                        <li key={l.url}>
                          <SiteLink testid={`drawer-service-${l.label.toLowerCase().replace(/\s/g, "-")}`} href={l.url} className="text-sm font-semibold text-neutral-600 hover:text-blue-700 transition-colors">
                            {l.label}
                          </SiteLink>
                        </li>
                      ))}
                    </ul>
                  </details>
                ))}
              </div>
              <div className="mt-auto">
                <SiteLink
                  testid="drawer-whatsapp"
                  href={waLink("*Print Livros — contato pelo site*\n\nOlá! Gostaria de mais informações.")}
                  className="w-full py-3.5 rounded-xl bg-[#25D366] text-white font-bold flex items-center justify-center gap-2"
                >
                  <MessageCircle size={17} /> Falar no WhatsApp
                </SiteLink>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
